import { GameSituation, HalfInning } from './types/game-situation.ts'
import { WinProbabilitySituationRow } from './types/win-probabilty.ts'

export function getGameSituation(): Partial<GameSituation> {
    // Inning + half
    const inningInput = document.querySelector<HTMLInputElement>(
        'input[name="inning"]:checked'
    )

    const situation =
        document.querySelector<HTMLSelectElement>('select[name="situation"]')
            ?.value ?? null

    const inning = inningInput ? Number(inningInput.value) : null

    const half = inningInput ? (inningInput.dataset.half as HalfInning) : null

    // Outs / Balls / Strikes
    const outs = Number(
        document.querySelector<HTMLInputElement>('input[name="outs"]:checked')
            ?.value ?? 0
    )

    const ballsInput = document.querySelector<HTMLInputElement>(
        'input[name="balls"]:checked'
    )
    const balls = ballsInput ? Number(ballsInput.value) : null
    const strikesInput = document.querySelector<HTMLInputElement>(
        'input[name="strikes"]:checked'
    )
    const strikes = strikesInput ? Number(strikesInput.value) : null

    // Run differential
    const runDiff =
        Number(
            document.querySelector<HTMLInputElement>(
                'input[name="diff"]:checked'
            )?.value ?? 0
        ) || 0

    // Runners
    const runners: GameSituation['runners'] = {
        '1b': false,
        '2b': false,
        '3b': false,
    }

    document.querySelectorAll<HTMLElement>('.base').forEach((base) => {
        const { id } = base // e.g. "runner-1b"
        if (!id) return

        const match = id.match(/-(1b|2b|3b)$/)
        if (!match) return

        const key = match[1] as keyof GameSituation['runners']
        runners[key] = base.dataset.selected === 'true'
    })

    const isPerspectiveChecked = document.querySelector<HTMLInputElement>(
        '#perspective-toggle'
    )?.checked

    // Use it in a ternary
    const perspective = isPerspectiveChecked ? 'bat' : 'home'

    const returnValue: Partial<GameSituation> = {
        inning,
        half,
        outs,
        balls,
        strikes,
        situation,
        run_diff: runDiff,
        runners,
        perspective,
    }

    return returnValue
}

// Resets game situation UI
export function resetGameSituation(): void {
    // Reset Inning to Top of the 1st
    const firstInningTop = document.querySelector<HTMLInputElement>(
        'input[name="inning"][value="1"]'
    )
    if (firstInningTop) {
        firstInningTop.checked = true
    }

    // Reset Score (Run Differential) to 0
    const zeroDiff = document.querySelector<HTMLInputElement>(
        'input[name="diff"][value="0"]'
    )
    if (zeroDiff) {
        zeroDiff.checked = true
    }

    // Reset Count: 0 Outs, 0 Balls, 0 Strikes
    const countSelectors = [
        'input[name="outs"][value="0"]',
        'input[name="balls"][value="0"]',
        'input[name="strikes"][value="0"]',
    ]

    countSelectors.forEach((selector) => {
        const el = document.querySelector<HTMLInputElement>(selector)
        if (el) {
            el.checked = true
        }
    })

    // Reset Situation Dropdown
    const situationSelect = document.querySelector<HTMLSelectElement>(
        'select[name="situation"]'
    )
    if (situationSelect) {
        situationSelect.value = ''
    }

    // Clear Runners
    document.querySelectorAll<HTMLElement>('.base').forEach((base) => {
        base.setAttribute('data-selected', 'false')
        base.setAttribute('aria-pressed', 'false')
        base.classList.remove('base-selected')
        base.classList.add('base-hatched')
    })

    // Reset Perspective Toggle
    const perspectiveToggle = document.querySelector<HTMLInputElement>(
        '#perspective-toggle'
    )
    if (perspectiveToggle) {
        perspectiveToggle.checked = true
    }
}

export function getGameSituationSummary(
    situationUpdate: WinProbabilitySituationRow | null
): string {
    const situation = getGameSituation()
    let { runners, run_diff, inning, half, outs, detailed_event_code } =
        situation

    if (situationUpdate) {
        run_diff = situationUpdate?.next_bat_score
        outs = situationUpdate.next_outs
        inning = situationUpdate.next_inning
        half = situationUpdate.next_bottom_top as HalfInning
        runners = computeRunners(situationUpdate.next_bases_cd || 0)
        detailed_event_code = situationUpdate.detailed_event_code || ''
    }

    let scoreText = ''
    if (run_diff !== null && run_diff !== undefined) {
        if (run_diff === 0) {
            scoreText = 'Tied'
        } else if (run_diff > 0) {
            scoreText = `Batting team up ${Math.abs(run_diff || 0)}`
        } else if (run_diff < 0) {
            scoreText = `Batting team down ${Math.abs(run_diff || 0)}`
        }
    }

    const inningText = `${half?.substring(0, 1)}${inning}`

    let runnerText = ''
    if (runners) {
        const r1 = runners['1b']
        const r2 = runners['2b']
        const r3 = runners['3b']

        const activeRunnersCount = [r1, r2, r3].filter(Boolean).length

        if (activeRunnersCount === 3) {
            runnerText = 'bases loaded'
        } else if (activeRunnersCount === 0) {
            runnerText = 'bases empty'
        } else {
            const bases = []
            if (r1) bases.push('1st')
            if (r2) bases.push('2nd')
            if (r3) bases.push('3rd')

            const label = bases.length > 1 ? 'runners' : 'runner'
            runnerText = `${label} on ${bases.join(' and ')}`
        }
    }

    return `${
        detailed_event_code ? `<b><u>${detailed_event_code}</b></u><br/>` : ``
    }${scoreText}, ${inningText}, ${runnerText}, ${outs} out, `
}

function computeRunners(bases_cd: number) {
    return {
        '1b': bases_cd % 2 === 1, // Remainder of 1 means 1st base is occupied
        '2b': Math.floor(bases_cd / 2) % 2 === 1, // Shift right, then check remainder
        '3b': Math.floor(bases_cd / 4) % 2 === 1, // Shift right twice, then check remainder
    }
}
